import { IAppContextMetadata } from 'src/types/IMetadata';

interface CachedMetadata {
    data: IAppContextMetadata;
    agGridLicense?: string; // AG Grid Enterprise license key
    timestamp: number;
    cacheDate: string; // YYYY-MM-DD format
}

const CACHE_KEY = 'origami_metadata_cache';

/**
 * Safe localStorage wrapper with error handling
 */
export const safeLocalStorage = {
    getItem: (key: string): string | null => {
        try {
            return localStorage.getItem(key);
        } catch (e) {
            console.error('localStorage.getItem failed', e);
            return null;
        }
    },

    setItem: (key: string, value: string): boolean => {
        try {
            localStorage.setItem(key, value);
            return true;
        } catch (e) {
            console.error('localStorage.setItem failed', e);
            return false;
        }
    },

    removeItem: (key: string): void => {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            console.error('localStorage.removeItem failed', e);
        }
    }
};

/**
 * Check if cached metadata should be refreshed
 * Refreshes when the calendar date has changed
 */
export function shouldRefreshCache(cachedData: CachedMetadata | null): boolean {
    if (!cachedData) return true;

    // Check if date has changed (using local timezone)
    const currentDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format

    return cachedData.cacheDate !== currentDate;
}

/**
 * Get cached metadata from localStorage
 */
export function getCachedMetadata(): CachedMetadata | null {
    try {
        const cached = safeLocalStorage.getItem(CACHE_KEY);
        if (!cached) return null;

        const cachedData: CachedMetadata = JSON.parse(cached);

        // Validate cache structure
        if (!cachedData.data || !cachedData.timestamp || !cachedData.cacheDate) {
            console.error('Invalid cache structure, clearing cache');
            clearMetadataCache();
            return null;
        }

        return cachedData;
    } catch (e) {
        console.error('Failed to parse cached metadata', e);
        clearMetadataCache();
        return null;
    }
}

/**
 * Save metadata to localStorage
 */
export function setCachedMetadata(metadata: IAppContextMetadata, agGridLicense?: string): boolean {
    try {
        const cacheData: CachedMetadata = {
            data: metadata,
            agGridLicense,
            timestamp: Date.now(),
            cacheDate: new Date().toLocaleDateString('en-CA') // YYYY-MM-DD format
        };

        return safeLocalStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
        console.error('Failed to cache metadata', e);
        return false;
    }
}

/**
 * Clear metadata cache
 */
export function clearMetadataCache(): void {
    safeLocalStorage.removeItem(CACHE_KEY);
}

/**
 * Get cache info for debugging/display
 */
export function getCacheInfo(): { exists: boolean; cacheDate?: string; timestamp?: number } {
    const cached = getCachedMetadata();
    if (!cached) {
        return { exists: false };
    }

    return {
        exists: true,
        cacheDate: cached.cacheDate,
        timestamp: cached.timestamp
    };
}
